import React, { useState } from 'react';

import clsx from 'clsx';
import { useForm, ValidateResult } from 'react-hook-form';

import { setAccount } from 'actions';
import { WarningImage } from 'assets/icons';
import BlurPopup from 'components/BlurPopup';
import { Button } from 'components/Buttons/Button';
import CodeVerificationForm from 'components/CodeVerificationForm';
import FormBody from 'components/FormBody';
import Input from 'components/Inputs/Input';
import PasswordInput from 'components/Inputs/PasswordInput';
import Loader from 'components/Loader';
import { AccountSettingsInputName } from 'enums/AccountSettingsInputName';
import { ButtonType } from 'enums/ButtonType';
import { ErrorsMessages } from 'enums/ErrorsMessages';
import { SuccessNotification } from 'enums/SuccessNotification';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import * as notificationManager from 'notifications';
import { updateBorrower } from 'thunks';
import { removeSpaces } from 'utils/stringUtils';
import { getMessageForRequiredFields, isValidEmail } from 'utils/validationUtils';
import { authApi } from 'api/auth/AuthApi';
import { ErrorDto } from 'errors/ErrorDto';
import { ErrorCodes } from 'enums/ErrorCodes';

import styles from './UpdateEmailPopup.module.scss';

interface ChangeEmailPopupProps {
  isOpen: boolean;
  onSuccess: () => void;
  onClose: () => void;
  validateEmail?: (value: string) => ValidateResult;
}

const UpdateEmailPopup = ({ isOpen, onClose, onSuccess, validateEmail }: ChangeEmailPopupProps) => {
  const { borrowerId } = useAppSelector((state) => state.borrower);
  const [isNeedToCheckPassword, setIsNeedToCheckPassword] = useState(true);
  const [isNeedToVerifyCode, setIsNeedToVerifyCode] = useState(false);
  const [isPasswordValidationTokenExpired, setIsPasswordValidationTokenExpired] = useState(false);
  const [passwordValidationToken, setPasswordValidationToken] = useState('');
  const [isVerifyPasswordLoading, setIsVerifyPasswordLoading] = useState<boolean>(false);
  const [isSendEmailCodeLoading, setIsSendEmailCodeLoading] = useState<boolean>(false);
  const [isCodeVerificationLoading, setIsCodeVerificationLoading] = useState<boolean>(false);
  const [isCodeValid, setIsCodeValid] = useState<boolean | null>(null);
  const dispatch = useAppDispatch();

  const {
    register,
    formState: { errors },
    setValue,
    trigger,
    handleSubmit,
    watch,
    setError,
  } = useForm();
  const watcher = watch();

  const validatePassword = async () => {
    if (watcher[AccountSettingsInputName.Password]) {
      try {
        setIsVerifyPasswordLoading(true);

        const result = await authApi.verifyPassword(watcher[AccountSettingsInputName.Password]);

        if (result.passwordValidationToken) {
          setIsPasswordValidationTokenExpired(false);
          setPasswordValidationToken(result.passwordValidationToken);
          setIsNeedToCheckPassword(false);
        } else {
          if (result.statusCode === 403 || result.statusCode === 400) {
            setError(AccountSettingsInputName.Password, {
              type: 'manual',
              message: 'Invalid Password. Please check your Password and try again',
            });
          } else {
            setError(AccountSettingsInputName.Password, {
              type: 'manual',
              message: 'Server error. Please try again later.',
            });
          }
        }
      } finally {
        setIsVerifyPasswordLoading(false);
      }
    }
  };

  const sendEmailCode = async () => {
    try {
      setIsCodeValid(null);
      setIsSendEmailCodeLoading(true);

      const response = await authApi.sendUpdateEmailVerificationCode({
        email: watcher[AccountSettingsInputName.NewEmail],
        accountPasswordValidationToken: passwordValidationToken,
      });

      if ((response as ErrorDto)?.error?.errorCode === ErrorCodes.EMAIL_IS_ALREADY_USED) {
        setIsSendEmailCodeLoading(false);
        setError(AccountSettingsInputName.NewEmail, {
          type: 'manual',
          message: 'This email is already used. Please use different email',
        });

        return;
      }

      setIsNeedToVerifyCode(true);
    } catch (error: any) {
      if (error.message === ErrorsMessages.PasswordTokenExpiredOrInvalid) {
        setIsNeedToCheckPassword(true);
        setIsNeedToVerifyCode(false);
        setIsPasswordValidationTokenExpired(true);
      }

      setError(AccountSettingsInputName.NewEmail, { type: 'manual', message: error.message });
    } finally {
      setIsSendEmailCodeLoading(false);
    }
  };

  const onSubmit = handleSubmit(async () => {
    if (isNeedToCheckPassword) {
      await validatePassword();

      return;
    }

    if (!isNeedToCheckPassword && !isNeedToVerifyCode) {
      await sendEmailCode();
    }
  });

  const handleVerificationCodeComplete = async (code: string) => {
    try {
      setIsCodeVerificationLoading(true);

      await authApi.updateEmailAddressByVerifyingCode(code);

      dispatch(setAccount({ email: watcher[AccountSettingsInputName.NewEmail] }));

      await dispatch(
        updateBorrower({
          email: watcher[AccountSettingsInputName.NewEmail],
        }),
      ).unwrap();
      notificationManager.notifySuccess(SuccessNotification.EmailChangedSuccessfully);
      onSuccess();

      setIsCodeVerificationLoading(false);

      setIsCodeValid(true);
    } catch (error) {
      setIsCodeVerificationLoading(false);
      setIsCodeValid(false);
    }
  };

  const getNewEmailInput = () => {
    if (!isNeedToCheckPassword && !isNeedToVerifyCode) {
      return (
        <Input
          label={`Please enter new Email Address`}
          errorMessage={errors[AccountSettingsInputName.NewEmail]?.message}
          name={AccountSettingsInputName.NewEmail}
          onChange={(event: any) => {
            setValue(AccountSettingsInputName.NewEmail, removeSpaces(event.target.value?.toLowerCase()));
            trigger(AccountSettingsInputName.NewEmail);
          }}
          inputRef={
            register(AccountSettingsInputName.NewEmail, {
              required: {
                value: true,
                message: getMessageForRequiredFields(AccountSettingsInputName.NewEmail),
              },
              validate: (value) => {
                if (!isValidEmail(value)) {
                  return 'Please enter a valid email address';
                }

                if (validateEmail) {
                  return validateEmail(value);
                }
              },
            }).ref
          }
          value={watcher[AccountSettingsInputName.NewEmail]}
        />
      );
    }
  };

  const getVerificationForm = () => {
    if (!isNeedToCheckPassword && isNeedToVerifyCode) {
      return (
        <CodeVerificationForm
          handleVerificationCodeComplete={handleVerificationCodeComplete}
          isCodeVerifying={isCodeVerificationLoading}
          isCodeValid={isCodeValid}
          alternativeFormHeader={
            <p className={styles.subtitle}>
              We've sent a 6-digit verification code to{' '}
              <a className={styles.email}>{watcher[AccountSettingsInputName.NewEmail]}</a>
            </p>
          }
          onResendCode={sendEmailCode}
        />
      );
    }
  };

  return (
    <BlurPopup blurDeep={2} onClose={onClose} open={isOpen}>
      <FormBody formBodyClassName={styles.formBody}>
        {isSendEmailCodeLoading || isVerifyPasswordLoading ? (
          <Loader containerWithoutMargin />
        ) : (
          <>
            <div className={styles.popupTitle}>Change Email Address</div>
            {isPasswordValidationTokenExpired && (
              <div className={styles.warningSubtitle}>
                <WarningImage />
                <div>Sorry, the time is out. Please try again</div>
              </div>
            )}
            {isNeedToCheckPassword && (
              <>
                <div className={styles.popupText}>Please enter your password to verify identity</div>
                <PasswordInput
                  name={AccountSettingsInputName.Password}
                  label={AccountSettingsInputName.Password}
                  errorMessage={errors[AccountSettingsInputName.Password]?.message}
                  onChange={(event: any) => {
                    setValue(AccountSettingsInputName.Password, event.target.value);

                    if (errors[AccountSettingsInputName.Password]?.message) {
                      trigger(AccountSettingsInputName.Password);
                    }
                  }}
                  onBlur={() => {
                    trigger(AccountSettingsInputName.Password);
                  }}
                  value={watcher[AccountSettingsInputName.Password]}
                  inputContainerClassName={styles.inputPasswordContainer}
                  register={register}
                  isErrorWithoutMessage={!!errors[AccountSettingsInputName.Password]?.message}
                />
              </>
            )}
            {getNewEmailInput()}
            {getVerificationForm()}
            <div className={clsx(styles.dialogButtonsContainer)}>
              {!isNeedToVerifyCode && (
                <Button type="submit" buttonType={ButtonType.Primary} onClick={onSubmit}>
                  Submit
                </Button>
              )}
              <Button buttonType={ButtonType.Secondary} onClick={onClose}>
                Cancel
              </Button>
            </div>
          </>
        )}
      </FormBody>
    </BlurPopup>
  );
};

export default UpdateEmailPopup;
